import { supabase } from '../lib/supabase';
import { User } from '../types/User';

export interface Connection {
  id: string;
  requester_id: string;
  recipient_id: string;
  status: 'pending' | 'accepted' | 'rejected';
  created_at: string;
  requester?: User;
  recipient?: User;
}

export interface Meeting {
  id: string;
  requester_id: string;
  recipient_id: string;
  meeting_date: string;
  time_slot: string;
  table_number: number;
  subject: string;
  description?: string;
  location?: string;
  status: 'pending' | 'confirmed' | 'rejected' | 'cancelled';
  created_at: string;
  requester?: User;
  recipient?: User;
}

export class NetworkingService {
  // Get participants (users) with pagination - simple version
  static async getParticipants(currentUserId: string, page: number = 0, pageSize: number = 30): Promise<{
    users: User[];
    hasMore: boolean;
    totalCount: number;
  }> {
    try {
      console.log(`Loading participants page ${page + 1} for user:`, currentUserId);
      
      const offset = page * pageSize;
      
      // Simple query without ORDER BY to avoid timeout
      const { data, error } = await supabase
        .from('users')
        .select('id, name, email, company, position, country, about, avatar_url, created_at')
        .range(offset, offset + pageSize - 1)
        .limit(pageSize);
          
      if (error) {
        console.error('Supabase query error in getParticipants:', error);
        return { users: [], hasMore: false, totalCount: 0 };
      }

      // Simple pagination logic without separate count query
      let totalCount = 0;
      let hasMore = false;
      
      if (data && data.length > 0) {
        const filteredData = data.filter(profile => 
          profile.id !== currentUserId && 
          profile.email !== 'admin@logistics.com'
        );
        
        // If we got a full page (30 users), assume there are more
        if (data.length === pageSize) {
          hasMore = true;
          totalCount = (page + 1) * pageSize + 1; // Estimate there's at least one more page
        } else {
          hasMore = false;
          totalCount = offset + filteredData.length;
        }
      }

      const users = (data || [])
        .filter(profile => 
          profile.id !== currentUserId && 
          profile.email !== 'admin@logistics.com'
        )
        .map(profile => ({
          id: profile.id,
          name: profile.name,
          email: profile.email,
          company: profile.company,
          position: profile.position,
          country: profile.country,
          about: profile.about,
          avatar: profile.avatar_url || undefined,
          role: 'user' as const,
        }));

      console.log(`Loaded ${users.length} users for page ${page + 1}, hasMore: ${hasMore}`);
      console.log(`Debug: data.length=${data?.length}, offset=${offset}, pageSize=${pageSize}`);

      return {
        users,
        hasMore,
        totalCount
      };
    } catch (error) {
      console.error('Unexpected error in getParticipants:', error);
      return { users: [], hasMore: false, totalCount: 0 };
    }
  }
    
  // Admin method to get all users without pagination restrictions
  static async getAllUsersForAdmin(): Promise<User[]> {
    try {
      console.log('Admin: Loading all users...');
      
      // Simple query without ORDER BY to avoid timeout
      const { data, error } = await supabase
        .from('users')
        .select('id, name, email, company, position, country, about, avatar_url, created_at');
          
      if (error) {
        console.error('Admin: Supabase query error:', error);
        return [];
      }

      const users = (data || [])
        .filter(profile => profile.email !== 'admin@logistics.com')
        .sort((a, b) => {
          // Sort by created_at descending (newest first) in JavaScript
          const dateA = new Date(a.created_at || 0);
          const dateB = new Date(b.created_at || 0);
          return dateB.getTime() - dateA.getTime();
        })
        .map(profile => ({
          id: profile.id,
          name: profile.name,
          email: profile.email,
          company: profile.company,
          position: profile.position,
          country: profile.country,
          about: profile.about,
          avatar: profile.avatar_url || undefined,
          role: 'user' as const,
        }));

      console.log(`Admin: Loaded ${users.length} users`);
      return users;
    } catch (error) {
      console.error('Admin: Unexpected error:', error);
      return [];
    }
  }
    
  // Legacy method for backward compatibility - now uses profiles table
  static async getAllParticipants(currentUserId: string): Promise<User[]> {
    const result = await this.getParticipants(currentUserId, 0, 50);
    return result.users;
  }

  // Send connection request
  static async sendConnectionRequest(requesterId: string, recipientId: string): Promise<{ success: boolean; error?: string; alreadyExists?: boolean }> {
    try {
      // Check if connection already exists (in either direction)
      const { data: existingConnections, error: checkError } = await supabase
        .from('connections')
        .select('id, status')
        .or(`and(requester_id.eq.${requesterId},recipient_id.eq.${recipientId}),and(requester_id.eq.${recipientId},recipient_id.eq.${requesterId})`);

      if (checkError) {
        return { success: false, error: checkError.message };
      }

      const existingConnection = existingConnections?.[0];

      if (existingConnection) {
        if (existingConnection.status === 'accepted') {
          // Already connected — treat as success (idempotent)
          return { success: true, alreadyExists: true };
        }

        // pending / rejected → accept (favorites is instant connect)
        const { error: updateError } = await supabase
          .from('connections')
          .update({ status: 'accepted' })
          .eq('id', existingConnection.id);

        if (updateError) {
          return { success: false, error: updateError.message };
        }

        return { success: true };
      }

      // First check if both users exist in the users table
      const { data: requesterExists } = await supabase
        .from('users')
        .select('id')
        .eq('id', requesterId)
        .maybeSingle();

      const { data: recipientExists } = await supabase
        .from('users')
        .select('id')
        .eq('id', recipientId)
        .maybeSingle();

      if (!requesterExists) {
        return { success: false, error: 'Requester not found in users table' };
      }

      if (!recipientExists) {
        return { success: false, error: 'Recipient not found in users table' };
      }

      const { error } = await supabase
        .from('connections')
        .insert({
          requester_id: requesterId,
          recipient_id: recipientId,
          status: 'accepted'
        });

      if (error) {
        // Race: another request inserted first — already connected
        if (error.code === '23505') {
          return { success: true, alreadyExists: true };
        }
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      return { success: false, error: 'Failed to send connection request' };
    }
  }

  // Get user connections. Retry is paused while the project is under load.
  static async getUserConnections(userId: string): Promise<Connection[]> {
    try {
      const { data, error } = await supabase
        .from('connections')
        .select(`
          id,
          requester_id,
          recipient_id,
          status,
          requester:users!connections_requester_id_fkey(id, name, avatar_url),
          recipient:users!connections_recipient_id_fkey(id, name, avatar_url)
        `)
        .or(`requester_id.eq.${userId},recipient_id.eq.${userId}`)
        .eq('status', 'accepted')
        .limit(50);

      if (error) {
        return [];
      }

      return (data || []).map(connection => ({
        ...connection,
        requester: connection.requester ? {
          ...connection.requester,
          avatar: connection.requester.avatar_url
        } : null,
        recipient: connection.recipient ? {
          ...connection.recipient,
          avatar: connection.recipient.avatar_url
        } : null
      }));
    } catch (error) {
      return [];
    }
  }

  // Schedule meeting
  static async scheduleMeeting(meetingData: {
    requester_id: string;
    recipient_id: string;
    meeting_date: string;
    time_slot: string;
    table_number: number;
    subject: string;
    description?: string;
    location?: string;
  }): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('meetings')
        .insert({
          ...meetingData,
          status: 'pending'
        });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      return { success: false, error: 'Failed to schedule meeting' };
    }
  }

  // Get user meetings
  static async getUserMeetings(userId: string): Promise<Meeting[]> {
    try {
      const { data, error } = await supabase
        .from('meetings')
        .select(`
          *,
          requester:users!meetings_requester_id_fkey(*),
          recipient:users!meetings_recipient_id_fkey(*)
        `)
        .or(`requester_id.eq.${userId},recipient_id.eq.${userId}`)
        .order('meeting_date', { ascending: true });

      if (error) {
        console.error('Error fetching meetings:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching meetings:', error);
      return [];
    }
  }

  // Update meeting status
  static async updateMeetingStatus(meetingId: string, status: 'confirmed' | 'rejected' | 'cancelled'): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('meetings')
        .update({ status })
        .eq('id', meetingId);

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      return { success: false, error: 'Failed to update meeting status' };
    }
  }

  // Update connection status
  static async updateConnectionStatus(connectionId: string, status: 'accepted' | 'rejected'): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('connections')
        .update({ status })
        .eq('id', connectionId);

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      return { success: false, error: 'Failed to update connection status' };
    }
  }
}